<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  target: { type: Object, default: null },
  balance: { type: Number, default: 0 },
  busy: Boolean,
  error: { type: String, default: '' },
  validation: { type: String, default: '' },
  toast: { type: String, default: '' },
})
const emit = defineEmits(['close', 'confirm'])
const dialog = ref(null)
const money = (value) => Number(value || 0).toFixed(2)
const total = computed(() => props.target?.entries.filter((row) => !row.done).reduce((sum, row) => sum + Math.round(Number(row.amount || 0) * 100), 0) / 100 || 0)
let previousFocus
let oldOverflow
function release() {
  if (oldOverflow !== undefined) document.body.style.overflow = oldOverflow
  oldOverflow = undefined
  previousFocus?.focus?.()
}
watch(() => props.target, async (target) => {
  if (!target) return release()
  previousFocus = document.activeElement
  if (oldOverflow === undefined) oldOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  await nextTick()
  const input = dialog.value?.querySelector('input:not(:disabled)')
  input?.focus()
  input?.select()
})
onBeforeUnmount(release)
watch(() => props.busy, async () => {
  await nextTick()
  if (!dialog.value) return
  // Keep keyboard focus inside the modal while every submit control is disabled.
  const focusTarget = props.busy ? dialog.value : dialog.value.querySelector('input:not(:disabled)')
  focusTarget?.focus()
})
function keydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); if (!props.busy) emit('close') }
  if (event.key !== 'Tab') return
  const controls = [...dialog.value.querySelectorAll('button:not(:disabled), input:not(:disabled)')]
  if (!controls.length) { event.preventDefault(); return }
  const first = controls[0], last = controls[controls.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="target" class="chat-follow-mask" @keydown="keydown">
      <section ref="dialog" class="chat-follow-dialog" role="dialog" tabindex="-1" aria-modal="true" aria-labelledby="chat-follow-title" :aria-busy="busy">
        <header class="chat-follow-head"><h2 id="chat-follow-title">跟投</h2><button type="button" class="chat-follow-close" :disabled="busy" aria-label="关闭" @click="emit('close')">×</button></header>
        <form class="chat-follow-content" @submit.prevent="emit('confirm')">
          <div class="chat-follow-source"><span>{{ target.player }}</span><span class="chat-follow-muted">{{ target.period }} 期</span></div>
          <div class="chat-follow-rows">
            <div v-for="(row, index) in target.entries" :key="index" class="chat-follow-row">
              <div class="chat-follow-detail"><span>{{ row.label }}</span><span class="chat-follow-muted">原注金额 {{ money(row.original) }}</span></div>
              <div class="chat-follow-label"><label :for="`chat-follow-amount-${index}`">{{ row.done ? '已跟投' : '跟投金额' }}</label><button v-if="!row.done" type="button" :disabled="busy" class="chat-follow-restore" @click="row.amount = money(row.original)">恢复原金额</button><span v-else class="chat-follow-done">成功</span></div>
              <div class="chat-follow-input"><span>¥</span><input :id="`chat-follow-amount-${index}`" v-model="row.amount" type="text" inputmode="decimal" autocomplete="off" :disabled="busy || row.done" :aria-invalid="!!validation && !row.done" aria-describedby="chat-follow-error"><span>元</span></div>
            </div>
          </div>
          <p id="chat-follow-error" v-if="validation || error" class="chat-follow-error" role="alert">{{ validation || error }}</p>
          <p class="chat-follow-balance">可用余额：{{ money(balance) }} 元</p>
          <p v-if="target.entries.length > 1" class="chat-follow-total">本次合计：{{ Number.isFinite(total) ? money(total) : '—' }} 元</p>
          <footer class="chat-follow-actions"><button type="button" :disabled="busy" @click="emit('close')">取消</button><button type="submit" class="chat-follow-confirm" :disabled="busy || !!validation">{{ busy ? '提交中…' : '确认跟投' }}</button></footer>
        </form>
      </section>
    </div>
    <div v-if="toast" class="chat-follow-toast" role="status">{{ toast }}</div>
  </Teleport>
</template>

<style scoped>
.chat-follow-mask{position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;padding:12px;background:rgb(0 0 0 / 40%);color:#263342;font:14px/1.5 "Microsoft YaHei",Arial,sans-serif}
.chat-follow-mask *{box-sizing:border-box}
.chat-follow-dialog{width:294px;max-width:100%;max-height:calc(100dvh - 24px);overflow:auto;background:#fff;border-radius:10px;box-shadow:0 8px 28px #0003}
.chat-follow-head{display:flex;align-items:center;justify-content:space-between;padding:9px 14px;border-bottom:1px solid #eee}
.chat-follow-head h2{font-size:15px;font-weight:500;margin:0;color:#222}
.chat-follow-mask button,.chat-follow-mask input{font:inherit}
.chat-follow-mask button{cursor:pointer}
.chat-follow-close{border:0;background:transparent;color:#7c858c;min-width:28px;min-height:28px;padding:0;font-size:22px!important}
.chat-follow-content{padding:12px 16px 16px}
.chat-follow-source{display:flex;justify-content:space-between;gap:8px;font-size:12px;margin-bottom:4px;overflow-wrap:anywhere}
.chat-follow-muted{color:#7a838d;font-size:12px}
.chat-follow-source .chat-follow-muted{flex-shrink:0}
.chat-follow-row+.chat-follow-row{margin-top:14px}
.chat-follow-detail{display:flex;flex-wrap:wrap;gap:4px 8px;padding-bottom:10px;margin-bottom:10px;border-bottom:1px solid #f0f0f0;font-size:12px;overflow-wrap:anywhere}
.chat-follow-label{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;font-size:12px}
.chat-follow-restore{border:0;background:transparent;color:#e83c49;padding:2px}
.chat-follow-input{display:flex;align-items:center;gap:6px;padding:0 10px;border:1px solid #edb3b8;border-radius:5px;background:#fff}
.chat-follow-input:focus-within{border-color:#ff414e;box-shadow:0 0 0 2px rgb(255 65 78 / 10%)}
.chat-follow-input input{width:100%;min-width:0;padding:7px 0;border:0;background:transparent;color:#202830;font-size:18px;font-weight:500}
.chat-follow-input input:disabled{color:#888}
.chat-follow-input input:focus{outline:none}
.chat-follow-error{font-size:12px;color:#dd2836;margin:5px 0 0;overflow-wrap:anywhere}
.chat-follow-balance{font-size:12px;color:#7a838d;margin:7px 0 0}
.chat-follow-total{font-size:12px;margin:7px 0 0;color:#e83c49}
.chat-follow-actions{display:flex;gap:8px;margin-top:14px}
.chat-follow-actions button{flex:1;border:1px solid #dedfe2;border-radius:5px;padding:8px;background:#fff;color:#6b737b;font-size:13px}
.chat-follow-actions .chat-follow-confirm{flex:2;border:0;background:#ff414e;color:#fff}
.chat-follow-mask button:disabled{cursor:default;opacity:.55}
.chat-follow-done{color:#20874e}
.chat-follow-toast{position:fixed;bottom:90px;left:50%;transform:translateX(-50%);z-index:1201;border-radius:5px;padding:9px 18px;background:rgb(0 0 0 / 78%);color:#fff;font-size:14px}
@media(pointer:coarse){.chat-follow-actions button,.chat-follow-close{min-height:44px}.chat-follow-restore{min-height:36px}}
</style>
